import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Bot, ChevronDown, Sparkles, X, ClipboardList, FileText, Folder, History, LayoutDashboard, ListTree, Lock, Percent, ShoppingCart, Table2 } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { laufFehlerText } from '@/lib/lauf-fehler'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige, frage } from '@/lib/dialoge'
import { pageWide } from '@/lib/form-styles'
import { api } from '@/lib/axios'
import { mitStand, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useTranslation } from 'react-i18next'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { ExportButton } from '@/components/ui/ExportButton'
import { Reiterleiste, type Reiter as ReiterEintrag } from '@/components/ui/Reiterleiste'
import { calculationPrintRequest, fetchOffers, type CalcOfferDocument } from '../api/calculation-offer.api'
import { changeCalculationStatus, copyCalculation, fetchCalculation, newRevision, submitCalculation } from '../api/calculations.api'
import { AngebotErstellenDialog } from '../components/AngebotErstellenDialog'
import { ProjektAnlegenDialog } from '../components/ProjektAnlegenDialog'
import { fetchCalculationProjects, type CalcProjectRow } from '../api/calculation-project.api'
import { useTerminology } from '@/lib/useTerminology'
import { useAssistent, useAssistentDatensatz, useDarfAssistent } from '@/lib/assistent/assistent.store'
import { useAenderung } from '@/lib/assistent/assistent-beilagen'
import { CalculationDataTab } from '../components/CalculationDataTab'
import { CalculationEventsTab } from '../components/CalculationEventsTab'
import { CalculationFilesTab } from '../components/CalculationFilesTab'
import { CalculationNavigator } from '../components/CalculationNavigator'
import { CalculationStatusDialog } from '../components/CalculationStatusDialog'
import { KalkulationsUebersicht } from '../components/KalkulationsUebersicht'
import { AssistentLeiste } from '../components/AssistentLeiste'
import { KiAuftraege } from '../components/KiAuftraege'
import { KiLaufDialog } from '../components/KiLaufDialog'
import { KiPruefung } from '../components/KiPruefung'
import { MaterialListeDialog } from '../components/MaterialListeDialog'
import { KiVorschlaege } from '../components/KiVorschlaege'
import { fetchKiLaeufe, type EingebauteFaehigkeit, type KiLauf } from '../api/calculation-ai.api'
import { LvEinlesenDialog } from '../components/LvEinlesenDialog'
import { NuBereich } from '../components/anfragen/NuBereich'
import { AngeboteUebernehmen } from '../components/anfragen/AngeboteUebernehmen'
import { LvTab } from '../components/LvTab'
import { PositionAnsicht, type PositionsAnsicht } from '../components/PositionAnsicht'
import { PositionenTab } from '../components/PositionenTab'
import { PositionenBereich } from '../components/PositionsListe'
import { SaetzeTab } from '../components/SaetzeTab'
import { STATUS_ANZEIGE, datumText, kundeText, prozentText, stundenText } from '../lib/kalkulation-labels'
import type { CalcFile, Calculation } from '../types/calculation.types'

/** N1/N6/N7: was ein eingebauter Lauf gerade tut — für die Anzeige im Kopf. */
const LAUF_TEXT: Record<string, string> = { V1: 'KI liest ein Angebot', V2: 'KI liest eine Stückliste', V6: 'KI schlägt Bieterlücken vor', CHECK: 'KI prüft die Kalkulation', ASSISTANT: 'Assistent' }

const BELEGSTATUS: Record<string, string> = { DRAFT: 'Entwurf', POSTED: 'festgeschrieben', SENT: 'versendet', CANCELLED: 'storniert' }

type Reiter = 'uebersicht' | 'daten' | 'liste' | 'positionen' | 'lv' | 'saetze' | 'dokumente' | 'verlauf'

/**
 * AP-37 9. — **eine Kalkulation**: Kopf (links Nummer · Fassung · Bezeichnung,
 * Kunde, Status, Abgabe; rechts Navigationsrahmen und Befehle), drei Kennzahlen,
 * Reiter. Phase C bringt Übersicht (Kalkulationsdaten), Dokumente und Verlauf;
 * Positionen (D), Sätze & Aufschläge (F) und LV (H) kommen mit ihren Phasen.
 */
export default function CalculationDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { user, hasModule } = useAuthStore()
  const { t } = useTranslation()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const belegwesen = hasModule('CORRESPONDENCE')
  // Detailblatt N 9.: offene KI-Vorschläge — nur mit Baustein .AI, manage und cost.view (N 14.).
  const kiDarf = hasModule('CALCULATION.AI') && darf('calculation.calculation.manage') && darf('calculation.cost.view')
  const [kiOffen, setKiOffen] = useState(0)
  const [kiZeigen, setKiZeigen] = useState(false)
  // AP-39 Nachtrag 2 (6.2): das Fenster „Angebote übernehmen" — der Preisspiegel dieser Kalkulation.
  const [angeboteZeigen, setAngeboteZeigen] = useState(false)
  // AP-18 Phase E: Wer den allgemeinen Assistenten hat, hat ihn auch hier — mit den Werkzeugen der
  // Kalkulation. Die eigene Leiste der Kalkulation bleibt für Mandanten ohne `AI_ASSISTANT` (AS-41).
  const allgemein = useDarfAssistent()
  const allgemeinOffen = useAssistent((s) => s.offen)
  const allgemeinUmschalten = useAssistent((s) => s.umschalten)
  // N8: Assistent als Seitenleiste; ob sie offen ist, merkt sich der Browser.
  const [assistent, setAssistentState] = useState(() => {
    try {
      return localStorage.getItem('kalkulation-assistent-offen') === '1'
    } catch {
      return false
    }
  })
  const [leiste, setLeiste] = useState(0)
  const setAssistent = (offen: boolean) => {
    setAssistentState(offen)
    try {
      localStorage.setItem('kalkulation-assistent-offen', offen ? '1' : '0')
    } catch {
      // ohne Speicher gilt „zu"
    }
  }
  const [angebote, setAngebote] = useState<CalcOfferDocument[]>([])
  const [angebotOffen, setAngebotOffen] = useState(false)
  // 4.15: Projekte aus dieser Kalkulation (Teilbeauftragung: mehrere).
  const terms = useTerminology()
  const projektmodul = hasModule('PROJECT_MGMT.CORE')
  const [projekte, setProjekte] = useState<CalcProjectRow[]>([])
  const [projektOffen, setProjektOffen] = useState(false)
  const [mitBlaettern, setMitBlaettern] = useState(false)
  const [lvOffen, setLvOffen] = useState(false)
  const lvBaustein = hasModule('CALCULATION.LV')
  const [k, setK] = useState<Calculation | null>(null)
  // AP-18: Der Assistent weiß, welche Kalkulation geöffnet ist.
  useAssistentDatensatz(k ? { art: 'calculation', id: k.id, bezeichnung: t('assistant.record.calculation', { number: k.label, name: k.name }) } : null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [menue, setMenue] = useState(false)
  const [verloren, setVerloren] = useState(false)
  // N1-21: Blatt oder Texte bleibt beim Wechsel der Position stehen.
  const [ansicht, setAnsicht] = useState<PositionsAnsicht>('blatt')
  const menueRef = useRef<HTMLDivElement>(null)
  const pos = params.get('pos')

  const laden = useCallback(() => {
    setFehler(null)
    fetchCalculation(id)
      .then(setK)
      .catch((e) => setFehler(apiErrorText(e, 'Die Kalkulation ließ sich nicht laden.')))
  }, [id])
  useEffect(laden, [laden])
  // N1 (10.2): laufende eingebaute Läufe — der Kopf zeigt sie, die Seite fragt alle 3 s nach.
  const [kiLaufend, setKiLaufend] = useState<KiLauf[]>([])
  const [kiFehler, setKiFehler] = useState<KiLauf[]>([])
  const [kiLauf, setKiLauf] = useState<{ faehigkeit: EingebauteFaehigkeit; datei?: CalcFile } | null>(null)
  const [liste, setListe] = useState<CalcFile | null>(null)
  const gesehen = useRef(new Set<string>())
  const kiStand = useCallback(() => {
    if (!kiDarf) return setKiOffen(0)
    fetchKiLaeufe(id)
      .then((r) => {
        setKiOffen(r.meta.openTotal)
        // Nur eingebaute Läufe: ein Auftrag für die eigene KI „läuft", bis sein Ergebnis kommt — Tage, ohne dass Symphenia arbeitet.
        const laufend = r.data.filter((l) => l.executor === 'SYMPHENIA' && (l.status === 'QUEUED' || l.status === 'RUNNING'))
        // Ein Lauf, den diese Seite laufen sah und der ohne Vorschläge endete, wird gemeldet.
        const gescheitert = r.data.filter((l) => gesehen.current.has(l.id) && (l.status === 'FAILED' || l.status === 'REJECTED'))
        if (gescheitert.length) setKiFehler((alt) => [...alt.filter((a) => !gescheitert.some((g) => g.id === a.id)), ...gescheitert])
        for (const l of r.data) if (l.status !== 'QUEUED' && l.status !== 'RUNNING') gesehen.current.delete(l.id)
        for (const l of laufend) gesehen.current.add(l.id)
        setKiLaufend(laufend)
      })
      .catch(() => setKiOffen(0))
  }, [id, kiDarf])
  useEffect(kiStand, [kiStand])
  useEffect(() => {
    if (!kiLaufend.length) return
    const t = setInterval(kiStand, 3000)
    return () => clearInterval(t)
  }, [kiLaufend.length, kiStand])
  // Übernommene Vorschläge ändern das offene Blatt — es wird neu aufgebaut (`blattStand`).
  const [blattStand, setBlattStand] = useState(0)
  const kiNeuLaden = useCallback(() => {
    laden()
    kiStand()
    setBlattStand((n) => n + 1)
  }, [laden, kiStand])
  // Vorschläge, die in der Leiste des allgemeinen Assistenten übernommen wurden, ändern diese Kalkulation.
  useAenderung('calculation', k?.id, kiNeuLaden)
  // „Starten" aus der Leiste des allgemeinen Assistenten: Der Dialog des Laufs gehört hierher (`?kilauf=`).
  const kiLaufWunsch = params.get('kilauf')
  useEffect(() => {
    if (!kiLaufWunsch) return
    if (kiDarf && (kiLaufWunsch === 'V6' || kiLaufWunsch === 'CHECK')) setKiLauf({ faehigkeit: kiLaufWunsch })
    setParams(
      (alt) => {
        const neu = new URLSearchParams(alt)
        neu.delete('kilauf')
        return neu
      },
      { replace: true },
    )
  }, [kiLaufWunsch, kiDarf, setParams])
  // 6.1 — Angebote aus dieser Kalkulation (alle Fassungen), neu gelesen mit dem Stand.
  useEffect(() => {
    if (!belegwesen || !darf('correspondence.document.read')) return setAngebote([])
    fetchOffers(id)
      .then(setAngebote)
      .catch(() => setAngebote([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, belegwesen, k?.updatedAt])
  useEffect(() => {
    if (!projektmodul || !darf('projects.project.read')) return setProjekte([])
    fetchCalculationProjects(id)
      .then(setProjekte)
      .catch(() => setProjekte([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projektmodul, k?.updatedAt])

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `calculation:${id}`,
    bezeichnung: k ? `Kalkulation ${k.label}` : 'Kalkulation',
    daten: k as unknown as Datensatz | null,
    schreibe: (patch, stand) =>
      api.patch<{ data: Calculation }>(`/calculations/${id}`, patch, mitStand(stand)).then((r) => {
        setK(r.data.data)
        return r.data as unknown as Datensatz
      }),
    keepalive: { url: `/calculations/${id}`, method: 'PATCH' },
  }) as unknown as Speicherstelle<unknown>

  useEffect(() => {
    if (!menue) return
    const weg = (e: MouseEvent) => menueRef.current && !menueRef.current.contains(e.target as Node) && setMenue(false)
    document.addEventListener('mousedown', weg)
    return () => document.removeEventListener('mousedown', weg)
  }, [menue])

  if (!k) return <div className="p-6 text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</div>

  const handlung = async (tu: () => Promise<Calculation>, weiter?: (n: Calculation) => void) => {
    setMenue(false)
    setFehler(null)
    try {
      const neu = await tu()
      if (weiter) weiter(neu)
      else setK(neu)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }
  const erlaubt = (s: Calculation['status']) => k.allowedTransitions.includes(s)
  const status = STATUS_ANZEIGE[k.status]
  // Einstieg (4.9, 4.10): mehr als eine Position oder Gruppen → Übersicht; eine Position → Positionen;
  // eine leere Kalkulation → Positionsübersicht mit „Erste Position“ und „LV einlesen“.
  const vorgabe: Reiter = k.counts.positions > 1 || k.counts.groups > 0 ? 'uebersicht' : k.counts.positions === 1 ? 'positionen' : 'liste'
  const reiter = (params.get('tab') as Reiter | null) ?? vorgabe
  const zeigeReiter = (r: Reiter) => setParams(r === vorgabe ? {} : { tab: r }, { replace: true })
  const oeffnePosition = (pid: string) => setParams({ tab: 'positionen', pos: pid })

  const menuePunkte: { text: string; tun: () => void; aus?: boolean; gefaehrlich?: boolean }[] = []
  if (!k.isTemplate) {
    if (erlaubt('SUBMITTED') && darf('calculation.calculation.submit')) {
      menuePunkte.push({
        text: 'Als abgegeben kennzeichnen',
        tun: async () => {
          if (!(await bestaetige('Als abgegeben kennzeichnen? Die Fassung wird festgeschrieben — Änderungen danach nur über eine neue Fassung.', { ok: 'Abgeben' }))) return
          void handlung(() => submitCalculation(k.id))
        },
      })
    }
    if (erlaubt('WON')) menuePunkte.push({ text: 'Beauftragt', tun: () => void handlung(() => changeCalculationStatus(k.id, { status: 'WON' })) })
    if (erlaubt('LOST')) menuePunkte.push({ text: 'Nicht beauftragt …', tun: () => (setMenue(false), setVerloren(true)) })
    if (erlaubt('DISCARDED')) {
      menuePunkte.push({
        text: 'Verwerfen',
        gefaehrlich: true,
        tun: async () => {
          if (!(await bestaetige('Kalkulation verwerfen? Die Nummer bleibt vergeben, weiter geht es nur über eine neue Fassung.', { ok: 'Verwerfen', gefaehrlich: true }))) return
          void handlung(() => changeCalculationStatus(k.id, { status: 'DISCARDED' }))
        },
      })
    }
    if (k.canRevise) {
      menuePunkte.push({
        text: 'Neue Fassung',
        tun: async () => {
          if (!(await bestaetige(`Neue Fassung F${Math.max(...k.revisions.map((f) => f.revision)) + 1} anlegen? Sie übernimmt alles aus ${k.label}; die jetzige bleibt als Verlauf lesbar.`, { ok: 'Neue Fassung' })))
            return
          void handlung(() => newRevision(k.id), (n) => navigate(`/calculations/${n.id}`))
        },
      })
    }
  }
  if (darf('calculation.calculation.manage')) {
    menuePunkte.push({
      text: k.isTemplate ? 'Als Kalkulation verwenden' : 'Kopieren als neue Kalkulation',
      tun: async () => {
        const name = await frage('Bezeichnung der neuen Kalkulation', k.name, { pflicht: true, ok: 'Anlegen' })
        if (name === null) return
        void handlung(() => copyCalculation(k.id, { name }), (n) => navigate(`/calculations/${n.id}`))
      },
    })
    if (!k.isTemplate) {
      menuePunkte.push({
        text: 'Als Vorlage',
        tun: async () => {
          const name = await frage('Bezeichnung der Vorlage', k.name, { pflicht: true, ok: 'Anlegen' })
          if (name === null) return
          void handlung(() => copyCalculation(k.id, { asTemplate: true, name }), (n) => navigate(`/calculations/${n.id}`))
        },
      })
    }
  }

  const kosten = darf('calculation.cost.view')
  // AP-39 Nachtrag 2 (6.2): Materialzeilen mit Angebot, das nicht übernommen ist — übernehmen kann nur, wer bearbeitet.
  const angeboteOffen = kosten && k.writable && darf('calculation.calculation.manage') ? (k.offersOpen ?? 0) : 0
  const pruefungKarte = (
    <KiPruefung
      kalkulationId={k.id}
      beschreibbar={k.writable}
      stand={`${kiLaufend.length}|${kiOffen}`}
      onStarten={() => setKiLauf({ faehigkeit: 'CHECK' })}
      onPosition={oeffnePosition}
      onGeaendert={kiStand}
    />
  )
  const reiterListe: ReiterEintrag<Reiter>[] = [
    { key: 'uebersicht', label: 'Übersicht', icon: LayoutDashboard },
    // N1-12: Kalkulationsdaten als eigener Reiter wie „Projektdaten“ — die Übersicht zeigt nur Zahlen.
    { key: 'daten', label: 'Kalkulationsdaten', icon: ClipboardList },
    // N1-18: das Raster aller Positionen und die Einzelansicht (Liste links) als zwei Reiter —
    // „Positionen“ auch bei nur einer Position (N2-1).
    { key: 'liste', label: 'Positionsübersicht', icon: Table2, zahl: k.counts.positions },
    ...(k.counts.positions > 0 ? [{ key: 'positionen' as const, label: 'Positionen', icon: ListTree }] : []),
    // Reiter LV nur mit Baustein `.LV` und eingelesenem LV (9.).
    ...(lvBaustein && k.hasLv ? [{ key: 'lv' as const, label: 'LV', icon: FileText }] : []),
    // Sätze & Aufschläge sind Kosten (7.): ohne `cost.view` kein Reiter.
    ...(kosten ? [{ key: 'saetze' as const, label: 'Sätze & Aufschläge', icon: Percent }] : []),
    { key: 'dokumente', label: 'Dokumente', icon: Folder, zahl: k.counts.files },
    { key: 'verlauf', label: 'Verlauf', icon: History },
  ]

  return (
    <div
      className={`${pageWide} ${assistent && kiDarf && !allgemein && !k.isTemplate ? 'lg:mr-[var(--assistent-breite)]' : ''}`}
      style={{ '--assistent-breite': `${leiste}px` } as CSSProperties}
    >
      {/* N1-9: Rücksprung dicht über dem Kopf, weniger Abstand nach oben. */}
      <div className="-mt-2 space-y-1 sm:-mt-3">
        <Link to={k.isTemplate ? '/calculations/templates' : '/calculations'} className="text-sm text-muted-foreground hover:text-foreground">
          ‹ {k.isTemplate ? 'Vorlagen' : 'Kalkulationen'}
        </Link>

        {/* Nachtrag 1 (N1-8): Kopf wie im Projekt — links Kennzeile und Kopfdaten (Kunde, Kalkulant,
            Abgabe, Angebote, Projekte), rechts der Navigationsrahmen und darunter die Befehle in einer Zeile. */}
        <div className="flex flex-col gap-4 rounded-lg border border-primary/30 bg-muted/20 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="truncate text-3xl font-bold text-foreground">
                <span className="font-normal">{k.number}</span> {k.name}
              </h1>
              <RevisionWahl k={k} />
              {!k.isTemplate && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.klasse}`}>{status.label}</span>}
              {k.isTemplate && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-900 dark:bg-violet-500/20 dark:text-violet-200">Vorlage</span>}
              {k.lockedAt && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={`festgeschrieben ${datumText(k.lockedAt, true)}${k.lockedByName ? ` von ${k.lockedByName}` : ''}`}>
                  <Lock className="h-3.5 w-3.5" /> festgeschrieben
                </span>
              )}
            </div>

            <dl className="flex flex-wrap gap-x-8 gap-y-1.5 text-sm">
              <Kopfzeile label="Kunde">{kundeText(k) || '—'}</Kopfzeile>
              <Kopfzeile label="Kalkulant">{k.ownerName ?? '—'}</Kopfzeile>
              {!k.isTemplate && <Kopfzeile label="Abgabe">{k.dueAt ? datumText(k.dueAt, true) : '—'}</Kopfzeile>}
              {angebote.length > 0 && (
                <Kopfzeile label="Angebote">
                  <span className="flex flex-wrap items-baseline gap-x-3">
                    {angebote.map((a) => (
                      <Link key={a.id} to={`/correspondence/${a.id}`} className="text-primary hover:underline">
                        {a.documentNumber ?? 'Angebot'} ({BELEGSTATUS[a.status] ?? a.status}
                        {a.revision !== k.revision ? `, aus F${a.revision}` : ''})
                      </Link>
                    ))}
                    {angebote.some((a) => a.changedAfter) && <span className="text-xs font-normal text-amber-700">Kalkulation nach dem Angebot geändert</span>}
                  </span>
                </Kopfzeile>
              )}
              {projekte.length > 0 && (
                <Kopfzeile label={terms.projectPlural}>
                  <span className="flex flex-wrap items-baseline gap-x-3">
                    {projekte.map((p) => (
                      <Link key={p.id} to={`/projects/${p.id}`} className="text-primary hover:underline">
                        {p.projectNumber} {p.name}
                      </Link>
                    ))}
                  </span>
                </Kopfzeile>
              )}
            </dl>

            {/* KI im Kopf statt als eigene Balken darunter (spart Höhe): offene Vorschläge, laufende Läufe, Fehler —
                und die Angebote der Lieferanten, die noch nicht übernommen sind (AP-39 Nachtrag 2, 6.2). */}
            {(kiOffen > 0 || kiLaufend.length > 0 || kiFehler.length > 0 || angeboteOffen > 0) && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {angeboteOffen > 0 && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2.5 py-1 text-emerald-800 hover:bg-emerald-500/20 dark:text-emerald-200"
                    onClick={() => setAngeboteZeigen(true)}
                    title="Angebote der Lieferanten vergleichen und in die Materialzeilen übernehmen"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    {angeboteOffen === 1 ? 'Ein Angebot nicht übernommen' : `${angeboteOffen} Angebote nicht übernommen`}
                    <span className="font-medium underline-offset-2 hover:underline">· übernehmen</span>
                  </button>
                )}
                {kiOffen > 0 && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-primary hover:bg-primary/20"
                    onClick={() => setKiZeigen(true)}
                    title="Vorschläge ansehen"
                  >
                    <Sparkles className="h-4 w-4" />
                    {kiOffen === 1 ? 'Ein KI-Vorschlag offen' : `${kiOffen} KI-Vorschläge offen`}
                    <span className="font-medium underline-offset-2 hover:underline">· ansehen</span>
                  </button>
                )}
                {kiLaufend.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-muted-foreground">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <KiArbeitet text={kiLaufend.map((l) => LAUF_TEXT[l.capability] ?? 'KI arbeitet').join(' · ')} />
                  </span>
                )}
                {kiFehler.map((l) => (
                  <span key={l.id} className="inline-flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-1 text-destructive">
                    KI-Lauf ohne Ergebnis: {laufFehlerText(l.error) || 'unbekannter Fehler'}
                    <button type="button" className="text-destructive/70 hover:text-destructive" aria-label="ausblenden" onClick={() => setKiFehler((alt) => alt.filter((a) => a.id !== l.id))}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={`flex shrink-0 flex-col items-end gap-2 ${kiDarf && !k.isTemplate ? '' : 'lg:w-[24rem]'}`}>
            <div className="flex w-full items-start justify-end gap-2">
              <CalculationNavigator kalkulation={{ id: k.id, label: k.label, name: k.name, isTemplate: k.isTemplate }} />
              {/* Detailblatt N 8., 15.: Symbol Assistent rechts neben dem Navigationsrahmen. */}
              {kiDarf && !k.isTemplate && (
                <button
                  type="button"
                  className={`inline-flex h-9 shrink-0 items-center gap-1 rounded-md border px-2.5 text-sm ${(allgemein ? allgemeinOffen : assistent) ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background'}`}
                  onClick={() => (allgemein ? allgemeinUmschalten() : setAssistent(!assistent))}
                  title="Assistent ein- und ausblenden"
                >
                  <Bot className="h-4 w-4" /> Assistent
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <ExportButton
                label="Ausdruck"
                defaultFilename={`kalkulation-${k.number}`}
                request={(format, orientation) => calculationPrintRequest(k.id, { format, orientation, sheets: mitBlaettern })}
                mail={{ source: 'EXPORT_CALCULATION', title: t('mailExport.titles.EXPORT_CALCULATION'), subject: `Kalkulation ${k.label} ${k.name}` }}
                extra={
                  <label className="flex items-center gap-2" title="Ausdruck mit Kostenzeilen und Arbeitsschritten je Position">
                    <input type="checkbox" checked={mitBlaettern} onChange={(e) => setMitBlaettern(e.target.checked)} /> mit Blättern
                  </label>
                }
              />
              {menuePunkte.length > 0 && (
                <div ref={menueRef} className="relative">
                  <button type="button" className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-background px-3 text-sm" onClick={() => setMenue((m) => !m)}>
                    Menü <ChevronDown className="h-4 w-4" />
                  </button>
                  {menue && (
                    <div className="absolute right-0 top-full z-50 mt-1 min-w-[240px] rounded-md border border-border p-1 shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
                      {menuePunkte.map((p) => (
                        <button key={p.text} type="button" className={`block w-full rounded px-3 py-1.5 text-left text-sm hover:bg-accent ${p.gefaehrlich ? 'text-destructive' : ''}`} onClick={p.tun}>
                          {p.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Mit LV ist das Angebot das ausgegebene LV (4.14) — kein „Angebot erstellen". */}
              {!k.isTemplate && !k.hasLv && belegwesen && darf('correspondence.offer.create') && darf('calculation.calculation.submit') && (
                <button type="button" className="h-9 rounded-md bg-primary px-3 text-sm text-primary-foreground" onClick={() => setAngebotOffen(true)}>
                  Angebot erstellen
                </button>
              )}
              {/* 4.15: angeboten, sobald die Kalkulation beauftragt ist. */}
              {!k.isTemplate && k.status === 'WON' && projektmodul && darf('projects.project.manage') && (
                <button type="button" className="h-9 rounded-md bg-primary px-3 text-sm text-primary-foreground" onClick={() => setProjektOffen(true)}>
                  {terms.project} anlegen
                </button>
              )}
            </div>
          </div>
        </div>

      </div>

      {kiZeigen && (
        <KiVorschlaege
          kalkulationId={k.id}
          darf={k.writable}
          onClose={() => setKiZeigen(false)}
          onGeaendert={kiNeuLaden}
        />
      )}
      {/* Übernommene Preise ändern das offene Blatt — `kiNeuLaden` baut es neu auf. */}
      {angeboteZeigen && <AngeboteUebernehmen kalkulation={k} onClose={() => setAngeboteZeigen(false)} onGeaendert={kiNeuLaden} />}
      {!k.isCurrent && (
        <div className="rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:bg-amber-500/20 dark:text-amber-200">
          Das ist nicht die aktuelle Fassung — nur zum Nachsehen.{' '}
          {k.revisions.find((f) => f.isCurrent) && (
            <Link className="underline" to={`/calculations/${k.revisions.find((f) => f.isCurrent)!.id}`}>
              Zur aktuellen Fassung
            </Link>
          )}
        </div>
      )}
      {k.isCurrent && k.lockedAt && (
        <div className="rounded-md bg-sky-500/10 px-3 py-2 text-sm text-sky-900 dark:text-sky-200">
          Diese Fassung ist abgegeben und nur lesbar. Für Änderungen im Menü „Neue Fassung" wählen; kopieren geht weiter.
        </div>
      )}
      {k.status === 'LOST' && k.lostReasonKey && (
        <div className="text-sm text-muted-foreground">
          Nicht beauftragt: {k.lostReasonName ?? k.lostReasonKey}
          {k.lostNote ? ` — ${k.lostNote}` : ''}
        </div>
      )}
      {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
      {/* N1-10: halber Abstand zum Kopf (space-y-6 → 3). */}
      <div className="!mt-3 grid gap-3 sm:grid-cols-3">
        <Kennzahl titel="Angebotssumme" wert={geldText(k.priceAfterDiscount) || '0,00'} unter={k.discountPercent ? `nach ${prozentText(k.discountPercent)} Nachlass` : 'netto'} />
        {kosten && <Kennzahl titel="Deckungsbeitrag" wert={prozentText(k.contributionMarginPercent ?? null) || '—'} unter={geldText(k.contributionMargin ?? null)} />}
        <Kennzahl titel="Stunden" wert={stundenText(k.hoursTotal)} unter={`${k.positionsPriced} ausgepreist · ${k.positionsOpen} offen`} />
      </div>

      <Reiterleiste reiter={reiterListe} aktiv={reiter} onWahl={zeigeReiter} />

      {/* N7: ohne LV steht die Prüfung vor der Abgabe in der Übersicht (mit LV in der Prüfliste des Reiters LV). */}
      {reiter === 'uebersicht' && kiDarf && !k.hasLv && !k.isTemplate && pruefungKarte}
      {reiter === 'uebersicht' && <KalkulationsUebersicht kalkulation={k} onGeaendert={laden} onSaetze={() => zeigeReiter('saetze')} onOeffnen={oeffnePosition} />}
      {/* K-Erzeuger: ohne LV geht das Teil-LV an Nachunternehmer als erzeugtes ONLV oder Excel. */}
      {reiter === 'uebersicht' && lvBaustein && !k.hasLv && !k.isTemplate && k.counts.positions > 0 && <NuBereich kalkulation={k} onGeaendert={laden} />}
      {reiter === 'daten' && <CalculationDataTab key={k.id} kalkulation={k} stelle={stelle} />}
      {reiter === 'saetze' && kosten && <SaetzeTab key={k.id} kalkulation={k} onGeaendert={laden} />}
      {/* Nachtrag 8 (N8-1): Die Positionsliste links ist entfallen — die geöffnete Position hat die ganze Breite,
          geblättert und gewählt wird an ihrer Überschrift (`PositionenBereich` reicht dem Kopf die Navigation). */}
      {reiter === 'positionen' && (
        <div>
          <PositionenBereich
            kalkulationId={k.id}
            aktuell={pos}
            // Neurechnen schreibt ohne `updatedAt` (D-1) — Zustand und EP ändern sich trotzdem.
            stand={`${k.updatedAt}|${k.recalculatedAt ?? ''}`}
            onOeffnen={oeffnePosition}
            onErste={(pid) => setParams({ tab: 'positionen', pos: pid }, { replace: true })}
            // Nachtrag 6 (N6-1): weitere Positionen auch hier anlegen — mit LV gibt das LV sie vor (N1-31).
            onAngelegt={
              k.writable && !k.hasLv && darf('calculation.calculation.manage')
                ? (pid) => {
                    laden()
                    oeffnePosition(pid)
                  }
                : undefined
            }
          >
            {(navigation, anlegen) =>
              pos ? (
                <PositionAnsicht ansicht={ansicht} onAnsicht={setAnsicht} key={`${pos}-${blattStand}`} kalkulation={k} positionId={pos} onGeaendert={laden} navigation={navigation} />
              ) : (
                k.counts.positions === 0 && (
                  <p className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    Noch keine Position — anlegen in der Positionsübersicht.
                    {anlegen && (
                      // N6-1: Ohne Position gibt es keinen Kopf und damit keine Aufklappliste — der Knopf steht hier.
                      <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground" onClick={anlegen}>
                        + Position
                      </button>
                    )}
                  </p>
                )
              )
            }
          </PositionenBereich>
        </div>
      )}
      {reiter === 'liste' && (
        <PositionenTab
          // Je Kalkulation ein eigener Reiter-Zustand: Was zugeklappt ist, merkt er sich je Kalkulation (N6-2).
          key={k.id}
          kalkulation={k}
          onGeaendert={laden}
          onOeffnen={oeffnePosition}
          lvEinlesen={lvBaustein && k.writable && darf('calculation.lv.import') ? () => setLvOffen(true) : undefined}
        />
      )}
      {reiter === 'lv' && lvBaustein && <LvTab
          kalkulation={k}
          onGeaendert={laden}
          onNeueFassung={(nid) => navigate(`/calculations/${nid}?tab=lv`)}
          kiLuecken={kiDarf && k.writable ? () => setKiLauf({ faehigkeit: 'V6' }) : undefined}
          kiPruefung={kiDarf ? pruefungKarte : undefined}
        />}
      {reiter === 'dokumente' && (
        <CalculationFilesTab
          kalkulation={k}
          onChanged={laden}
          kiLesen={kiDarf && k.writable && !k.isTemplate ? (datei) => setKiLauf({ faehigkeit: 'V1', datei }) : undefined}
          listeLesen={
            kiDarf && k.writable && !k.isTemplate ? (datei) => (datei.file.mimeType === 'application/pdf' ? setKiLauf({ faehigkeit: 'V2', datei }) : setListe(datei)) : undefined
          }
        />
      )}
      {liste && (
        <MaterialListeDialog
          kalkulationId={k.id}
          datei={liste}
          onClose={() => setListe(null)}
          onFertig={() => {
            setListe(null)
            kiStand()
            setKiZeigen(true)
          }}
        />
      )}
      {kiLauf && (
        <KiLaufDialog
          kalkulationId={k.id}
          faehigkeit={kiLauf.faehigkeit}
          datei={kiLauf.datei}
          onClose={() => setKiLauf(null)}
          onGestartet={() => {
            setKiLauf(null)
            kiStand()
          }}
        />
      )}
      {/* Detailblatt N 3a.4 (N-X): Aufträge für die eigene KI — mit Baustein .AI, manage und cost.view. */}
      {reiter === 'dokumente' && kiDarf && !k.isTemplate && <KiAuftraege key={params.get('ki') ?? ''} kalkulation={k} vorwahl={params.get('ki')} onGeaendert={kiNeuLaden} />}
      {assistent && kiDarf && !allgemein && !k.isTemplate && (
        <AssistentLeiste
          kalkulationId={k.id}
          beschreibbar={k.writable}
          onClose={() => setAssistent(false)}
          onGeaendert={kiNeuLaden}
          onNeueVorschlaege={kiStand}
          onPosition={oeffnePosition}
          onAuftrag={(f) => (f === 'V6' || f === 'CHECK' ? setKiLauf({ faehigkeit: f }) : setParams({ tab: 'dokumente', ki: f }))}
          onBreite={setLeiste}
        />
      )}
      {reiter === 'verlauf' && <CalculationEventsTab kalkulationId={k.id} stand={k.updatedAt} />}

      {lvOffen && (
        <LvEinlesenDialog
          kalkulation={k}
          onClose={() => setLvOffen(false)}
          onDone={() => {
            setLvOffen(false)
            laden()
            setParams({ tab: 'lv' })
          }}
        />
      )}
      {angebotOffen && (
        <AngebotErstellenDialog
          kalkulation={k}
          onClose={() => setAngebotOffen(false)}
          onDone={(documentId) => {
            setAngebotOffen(false)
            navigate(`/correspondence/${documentId}`)
          }}
        />
      )}
      {projektOffen && (
        <ProjektAnlegenDialog
          kalkulation={k}
          onClose={() => setProjektOffen(false)}
          onDone={(r) => {
            setProjektOffen(false)
            // Einfügen im Projekt: Der Dialog öffnet sich dort mit den Kalkulationspositionen.
            navigate(`/projects/${r.projectId}?tab=positions&paste=1`)
          }}
        />
      )}
      {verloren && (
        <CalculationStatusDialog
          kalkulation={k}
          onClose={() => setVerloren(false)}
          onDone={(n) => {
            setVerloren(false)
            setK(n)
          }}
        />
      )}
    </div>
  )
}

function Kopfzeile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children}</dd>
    </div>
  )
}

/** N1-9: höchstens zwei Zeilen — Titel, darunter Wert mit der Erläuterung daneben. */
function Kennzahl({ titel, wert, unter }: { titel: string; wert: string; unter?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</div>
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="text-xl font-semibold tabular-nums">{wert}</span>
        {unter && <span className="truncate text-xs text-muted-foreground">{unter}</span>}
      </div>
    </div>
  )
}

/** Fassung: `F2 · aktuell`, ausklappbar zu den übrigen Fassungen. */
function RevisionWahl({ k }: { k: Calculation }) {
  const [offen, setOffen] = useState(false)
  const navigate = useNavigate()
  if (k.isTemplate) return null
  const text = `F${k.revision}${k.isCurrent ? ' · aktuell' : ''}`
  if (k.revisions.length < 2) return <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">{text}</span>
  return (
    <span className="relative">
      <button type="button" className="inline-flex items-center gap-0.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground" onClick={() => setOffen((o) => !o)}>
        {text} <ChevronDown className="h-3 w-3" />
      </button>
      {offen && (
        <span className="absolute left-0 top-full z-50 mt-1 block min-w-[220px] rounded-md border border-border p-1 shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
          {k.revisions.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`flex w-full justify-between gap-3 rounded px-2 py-1 text-left text-sm hover:bg-accent ${f.id === k.id ? 'font-medium' : ''}`}
              onClick={() => {
                setOffen(false)
                navigate(`/calculations/${f.id}`)
              }}
            >
              <span>F{f.revision}</span>
              <span className="text-xs text-muted-foreground">
                {STATUS_ANZEIGE[f.status].label}
                {f.isCurrent ? ' · aktuell' : ''}
              </span>
            </button>
          ))}
        </span>
      )}
    </span>
  )
}
